import type { GameState, GameStats, LandedPod } from "@/types/game.types"
import type { Tower } from "@/types/tower.types"
import type { Hero, HeroType } from "@/types/hero.types"
import type { SummonedUnit } from "@/types/summon.types"
import type { HeroInventory } from "@/types/item.types"
import type { UpgradeState } from "@/types/upgrade.types"
import type { MapState } from "@/types/map.types"

const GAME_STATE_KEY = "pelf_pending_game_state"

export interface PersistedGameState {
  gameState: GameState
  stats: GameStats
  towers: Tower[]
  hero: Hero | null
  selectedHeroType: HeroType
  currentLevelIndex: number
  currentWaveIndex: number
  showNextWaveButton: boolean
  freePremiumTowers: number
  landedPod: LandedPod | null
  summonedUnits: SummonedUnit[]
  // Optional (added later) - older saves may not have these
  heroInventory?: HeroInventory
  upgradeState?: UpgradeState
  mapState?: MapState | null
  heroismLevel?: number
  timestamp: number
}

export function saveGameStateForAuth(state: {
  gameState: GameState
  stats: GameStats
  towers: Tower[]
  hero: Hero | null
  selectedHeroType: HeroType
  currentLevelIndex: number
  currentWaveIndex: number
  showNextWaveButton: boolean
  freePremiumTowers: number
  landedPod: LandedPod | null
  summonedUnits: SummonedUnit[]
  // Optional (added later) - older saves may not have these
  heroInventory?: HeroInventory
  upgradeState?: UpgradeState
  mapState?: MapState | null
  heroismLevel?: number
}): void {
  if (typeof window === "undefined") return

  const persistedState: PersistedGameState = {
    ...state,
    timestamp: Date.now(),
  }

  try {
    sessionStorage.setItem(GAME_STATE_KEY, JSON.stringify(persistedState))
  } catch (error) {
    console.error("Failed to save game state:", error)
  }
}

export function loadGameStateAfterAuth(): PersistedGameState | null {
  if (typeof window === "undefined") return null

  try {
    const stored = sessionStorage.getItem(GAME_STATE_KEY)
    if (!stored) return null

    const state = JSON.parse(stored) as PersistedGameState

    // Only restore if saved within the last 10 minutes
    const tenMinutes = 10 * 60 * 1000
    if (Date.now() - state.timestamp > tenMinutes) {
      clearSavedGameState()
      return null
    }

    return state
  } catch (error) {
    console.error("Failed to load game state:", error)
    return null
  }
}

export function clearSavedGameState(): void {
  if (typeof window === "undefined") return
  sessionStorage.removeItem(GAME_STATE_KEY)
}

export function hasSavedGameState(): boolean {
  if (typeof window === "undefined") return false
  return sessionStorage.getItem(GAME_STATE_KEY) !== null
}

// ============================================
// PENDING RUN SNAPSHOT (crash/eviction recovery)
// ============================================
// Mobile browsers (iOS Safari especially) evict background tabs freely, which
// used to cost the whole run. During play the game snapshots itself here every
// few seconds and on pagehide; the title screen offers to resume it.
// localStorage (not sessionStorage) so it survives the tab being killed.

const PENDING_RUN_KEY = "pelf_pending_run"
const PENDING_RUN_TTL_MS = 24 * 60 * 60 * 1000 // resumable for a day

export function savePendingRun(state: Omit<PersistedGameState, "timestamp">): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(
      PENDING_RUN_KEY,
      JSON.stringify({ ...state, timestamp: Date.now() } satisfies PersistedGameState),
    )
  } catch {
    // Storage full/blocked - run recovery just won't be available
  }
}

export function loadPendingRun(): PersistedGameState | null {
  if (typeof window === "undefined") return null
  try {
    const stored = window.localStorage.getItem(PENDING_RUN_KEY)
    if (!stored) return null
    const state = JSON.parse(stored) as PersistedGameState
    if (Date.now() - state.timestamp > PENDING_RUN_TTL_MS) {
      clearPendingRun()
      return null
    }
    // Sanity: a run without a hero isn't resumable
    if (!state.hero) return null
    return state
  } catch {
    return null
  }
}

export function clearPendingRun(): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.removeItem(PENDING_RUN_KEY)
  } catch {
    /* ignore */
  }
}
